/* eslint-disable jsx-a11y/media-has-caption */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '~/contexts/ThemeContext'
import { useSiteVideoQuery } from "~/hooks/useSiteVideoQuery"
import { inViewPartLarge } from '~/utils/helpers'

import * as styles from "~/styles/blog/vplay.module.scss"
import { aria_btn } from "~/styles/app.module.scss"

const VideoControls = ({ handle, par_styles, loopAuto=false }) => {
  const { isMobile } = useTheme()

  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const progRef = useRef(null);

  const videojs = ((typeof window !== 'undefined') && window.videojs) || null
  
  const [initHeight, setInitHeight] = useState('50vh')
  const [player, setPlayer] = useState(null);
  const [prog, setProg] = useState(false);
  const [showPlay, setShowPlay] = useState(true);
  const [showPause, setShowPause] = useState(false);
  const [showMute, setShowMute] = useState(true);
  const [fullOn, setFullOn] = useState(false);
  const [autoMute, setAutoMute] = useState(true);

  const video_item = useSiteVideoQuery(handle)

  const handlePlay = () => {
    setShowPlay(false)
    setShowPause(true)
    setShowMute(false)
    setProg(true)

    if (!player.paused()) return
    player.muted(false)
    player.play()
  }

  const handlePause = () => {
    setShowPlay(true)
    setShowPause(false)
    setShowMute(true)

    if (player.paused()) return
    player.pause()
  }

  const handleMute = () => {
    setShowMute(!showMute)
    player.muted(!showMute)
  }

  const handleAutoMute = () => {
    setAutoMute(false)
    setShowMute(false)
    player.muted(false)
  }

  const scrubClick = (e) => {
    if (!wrapRef.current) return
    const wrapbox = e.target.getBoundingClientRect()
    const w_left = wrapbox.left
    const w_wid = wrapbox.width
    const dur = player.duration()
    const perc = (e.screenX - w_left) / w_wid
    progRef.current.style.transition = 'none'
    player.currentTime(Math.round(perc * dur))
  }

  const handleFull = () => {
    const isfull = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement
    if (isfull) {
      closeFull()
      setTimeout(()=>{
        player.play()
      }, 200)
      return
    }
    setFullOn(true)
    isMobile ? player.requestFullscreen() : wrapRef.current.requestFullscreen() 
  }

  const closeFull = () => {
    const isfull = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement
    if (!isfull) {
      handleFull()
      return
    }
    setFullOn(false)
    document.exitFullscreen()
  }

  const checkScroll = (e) => {
    const el = videoRef.current
    if (!el) return

    const is_inview = inViewPartLarge(el)

    if (!player) return
    if (is_inview && player.paused()) (isMobile && el) ? el.play() : player.play()
    if (!is_inview) (isMobile && el) ? el.pause() : player.pause()
  }

  useEffect(() => {
    if (!player) return
    const el = wrapRef.current
    player.on('loadeddata', function () {
      if (el) el.style.minHeight = null
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player])


  useEffect(() => {
    if (isMobile !== null && isMobile) setInitHeight('70vh')
    if (player) return
    if (!videojs) return

    const media = video_item.media
    const options = {
      controls: false,
      children: ['mediaLoader', 'posterImage'],
      playsinline: true,
      setPlaysinline: true,
      defaultmuted: true,
      sources: media.sources.filter(x => x.mimeType === 'application/x-mpegURL').map((x) => {
        return {src: x.url, type: x.mimeType}
      })
    }
    if (media.src) {
      options['poster'] = media.src
    }
    if ( loopAuto === true ) {
      options['muted'] = true
      options['loop'] = true
      options['autoplay'] = true
    }

    setPlayer(videojs(videoRef.current, options))

    return () => {
      if (player) {
        player.dispose();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])



  useEffect(() => {
    if (prog === false || !videojs) return

    const vid_time = setInterval(() => {
      if (!player) return

      const pos = (player.currentTime() / player.duration()) * 100

      setProg(pos)

      if (Math.round(pos) === 100) {
        if (loopAuto === true ) {
          player.currentTime(1) 
        } else {
          player.pause()
          setShowPlay(true)
          setShowPause(false)
          setShowMute(true)
          player.currentTime(0) 
          setProg(false)
          clearInterval(vid_time)
        }
      }

      if (fullOn) {
        const vid_el = document.querySelector(`[vid="${handle}"] video`)
        if (!player.isFullscreen() && vid_el.paused) {
          handlePause()
          setFullOn(false)
          setTimeout(()=>{
            handlePlay()
          }, 500)
        }
      }
    }, 50); 

    return () => {
      clearInterval(vid_time);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prog]);

  useEffect(() => {
    if (!player) return

    if (player && loopAuto === true ) {
      setProg(true)
      setShowPlay(false)
      setShowPause(true)
      setShowMute(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player])

  useEffect(() => {
    window.addEventListener('scroll', checkScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', checkScroll)
    }
  })


  return ( 
    <figure 
      className={`${styles.vplay} ${par_styles} ${autoMute ? styles.d_automute : ''}`} 
      ref={wrapRef}
      style={{minHeight: initHeight}}
    >
      <video ref={videoRef} className="video-js" vid={handle} />
      {autoMute && (
        <button 
          type="button"
          className={[aria_btn, styles.vplay_btn, styles.vplay_automute].join(' ')} 
          aria-label="Mute video" 
          onClick={handleAutoMute}
        >
          <div className={styles.automute_ico}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <path d="M59.6,7.3c0-0.8-1-1.3-1.6-0.8L35,24.7c-0.2,0.1-0.4,0.2-0.6,0.2H12c-0.6,0-1,0.5-1,1v28.5c0,0.6,0.4,1,1,1l10.7,0l37-37V7.3z"/>
              <path d="M25.5,55.3l8.8,0c0.2,0,0.4,0.1,0.6,0.2v0l23,18c0.7,0.5,1.6,0,1.6-0.8V21.2L25.5,55.3z"/>
              <line x1="24.1" y1="55.3" x2="6.9" y2="72.6"/>
              <line x1="73.1" y1="6.3" x2="59.6" y2="19.8"/>
            </svg>
          </div>
        </button>
      )}
      {!autoMute && (
        <>
          {showPlay && (
            <button 
              type="button"
              className={[aria_btn, styles.vplay_btn, styles.vplay_bigplay].join(' ')} 
              aria-label="Play video" 
              onClick={handlePlay}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
                <polygon points="13.83 7.31 13.83 72.69 66.17 40 13.83 7.31"/>
              </svg>
            </button>
          )}
          {showPause && (
            <button 
              type="button"
              className={[aria_btn, styles.vplay_btn, styles.vplay_bigplay].join(' ')} 
              aria-label="Pause video" 
              onClick={handlePause}
            >
            </button>
          )}
        </>
      )}
      <figcaption className={styles.vplay_cap}>
        {showPlay && (
          <button 
            type="button"
            className={[aria_btn, styles.vplay_btn].join(' ')} 
            aria-label="Play video" 
            onClick={handlePlay}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <polygon points="13.83 7.31 13.83 72.69 66.17 40 13.83 7.31"/>
            </svg>
          </button>
        )}
        {showPause && (
          <button 
            type="button"
            className={[aria_btn, styles.vplay_btn].join(' ')} 
            aria-label="Pause video" 
            onClick={handlePause}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <rect x="19.2" y="7.31" width="14.29" height="65.38"/>
              <rect x="46.51" y="7.31" width="14.29" height="65.38"/>
            </svg>
          </button>
        )}
        <div className={styles.vplay_trackwr}>
          <button 
            type="button"
            className={[aria_btn, styles.vplay_track].join(' ')} 
            onPointerUp={(e)=> scrubClick(e)} 
            ref={trackRef}
            aria-label={`Video is ${player?.duration()} seconds long, currently at ${prog} percent`}
          />
          <button 
            type="button"
            className={[aria_btn, styles.vplay_prog].join(' ')} 
            style={{left: `${prog}%`}}
            ref={progRef}
            aria-label={`Video currently at ${prog} percent`}
          />
        </div>
        <button 
          type="button"
          className={[aria_btn, styles.vplay_btn].join(' ')} 
          aria-label="Mute video" 
          onClick={(e)=>handleMute()}
        >
          {showMute ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <path d="m34.98,55.51l23.04,18c.66.51,1.62.05,1.62-.79V7.28c0-.84-.96-1.3-1.62-.79l-23.04,18.16c-.18.14-.39.21-.62.21H11.96c-.55,0-1,.45-1,1v28.49c0,.55.45,1,1,1l22.4-.06c.22,0,.44.07.62.21Z"/>
              <line x1="73.14" y1="6.27" x2="6.86" y2="72.55"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <path d="m28.09,55.51l23.04,18c.66.51,1.62.05,1.62-.79V7.28c0-.84-.96-1.3-1.62-.79l-23.04,18.16c-.18.14-.39.21-.62.21H5.06c-.55,0-1,.45-1,1v28.49c0,.55.45,1,1,1l22.4-.06c.22,0,.44.07.62.21Z"/>
              <line x1="64.79" y1="23.41" x2="64.79" y2="50.59"/>
              <line x1="75.94" y1="14.35" x2="75.94" y2="59.65"/>
            </svg>
          )}
        </button>
        {fullOn ? (
          <button 
            type="button"
            className={[aria_btn, styles.vplay_btn].join(' ')} 
            aria-label="Make video fullscreen" 
            onClick={(e)=> closeFull()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <polyline points="7.31 33.99 7.31 7.31 33.99 7.31"/>
              <polyline points="33.99 72.69 7.31 72.69 7.31 46.01"/>
              <polyline points="72.69 46.01 72.69 72.69 46.01 72.69"/>
              <polyline points="46.01 7.31 72.69 7.31 72.69 33.99"/>
            </svg>
          </button>
        ) : (
          <button 
            type="button"
            className={[aria_btn, styles.vplay_btn].join(' ')} 
            aria-label="Make video fullscreen" 
            onClick={(e)=> handleFull()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
              <polyline points="7.31 33.99 7.31 7.31 33.99 7.31"/>
              <polyline points="33.99 72.69 7.31 72.69 7.31 46.01"/>
              <polyline points="72.69 46.01 72.69 72.69 46.01 72.69"/>
              <polyline points="46.01 7.31 72.69 7.31 72.69 33.99"/>
            </svg>
          </button>
        )}
      </figcaption>
    </figure>
  );
};

export default VideoControls

